import { useEffect, useState } from 'react';
import { Mail, Send, Clock, TrendingUp, Users, DollarSign, AlertTriangle, Heart } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('masterToken');
  return { Authorization: `Bearer ${token}` };
};

export default function MasterDigest() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [runs, setRuns] = useState([]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/master/digest/preview?hours=${hours}`, { headers: authHeaders() });
      if (res.ok) setPreview(await res.json());
      else toast.error('Failed to load preview');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  const loadRuns = async () => {
    try {
      const res = await fetch(`${API}/master/digest/runs?limit=20`, { headers: authHeaders() });
      if (res.ok) {
        const body = await res.json();
        setRuns(body.runs || []);
      }
    } catch {}
  };
  useEffect(() => { loadPreview(); loadRuns(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hours]);

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/master/digest/send-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ hours, recipient: recipient.trim() || undefined }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || 'Send failed');
      toast.success(`Digest sent to ${body.recipient} (${body.status})`);
      loadRuns();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="master-digest">
      <div>
        <h1 className="font-syne text-2xl font-bold text-white flex items-center gap-2">
          <Mail className="text-[#A78BFA]" size={24} /> Daily Digest
        </h1>
        <p className="text-[#9CA3AF] text-sm mt-1">
          Automatically sent to <span className="text-[#C4B5FD]">master@entreprenatives.com</span> at <b>6:00 AM PT</b> every day. Send a one-off snapshot anytime below.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-[#1E1E3A] border border-white/10 rounded-2xl p-5 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-[#9CA3AF] text-xs uppercase tracking-wider">Window (hours)</label>
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl p-2.5 text-white text-sm focus:outline-none focus:border-[#8B5CF6]"
              data-testid="digest-hours"
            >
              <option value={24}>Last 24h</option>
              <option value={48}>Last 48h</option>
              <option value={72}>Last 72h</option>
              <option value={168}>Last 7 days</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[#9CA3AF] text-xs uppercase tracking-wider">Recipient (optional · defaults to master)</label>
            <input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="leave blank to send to master@entreprenatives.com"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl p-2.5 text-white text-sm focus:outline-none focus:border-[#8B5CF6]"
              data-testid="digest-recipient"
            />
          </div>
        </div>
        <button
          onClick={sendNow}
          disabled={sending}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="digest-send-now"
        >
          <Send size={14} /> {sending ? 'Sending…' : 'Send digest now'}
        </button>
      </div>

      {/* Preview cards */}
      {loading ? (
        <div className="text-[#9CA3AF] text-center p-8">Loading preview…</div>
      ) : preview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={<DollarSign size={16} />} label="Receipts" value={`$${(preview.receipts_total || 0).toFixed(2)}`} color="text-emerald-400" />
            <Stat icon={<Heart size={16} />} label="Tips" value={`$${(preview.tip_total || 0).toFixed(2)}`} color="text-pink-400" />
            <Stat icon={<Users size={16} />} label="New Users" value={preview.new_users} color="text-[#A78BFA]" />
            <Stat icon={<TrendingUp size={16} />} label="Orders" value={preview.new_bookings} color="text-blue-400" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-[#1E1E3A] border border-amber-500/20 rounded-2xl p-5">
              <h3 className="text-amber-300 font-syne font-bold text-sm uppercase tracking-widest mb-3">Founding Vendors · {preview.fv_taken}/50</h3>
              {(preview.new_founders || []).length === 0 ? (
                <p className="text-[#64748B] text-sm italic">None in this window.</p>
              ) : preview.new_founders.map((f) => (
                <div key={f.vendor_id} className="py-1.5 text-sm flex gap-3">
                  <span className="text-amber-400 font-bold w-8">#{f.founding_vendor_number}</span>
                  <span className="text-white flex-1">{f.business_name}</span>
                  <span className="text-[#9CA3AF] text-xs">{f.owner_name}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#1E1E3A] border border-emerald-500/20 rounded-2xl p-5">
              <h3 className="text-emerald-300 font-syne font-bold text-sm uppercase tracking-widest mb-3">Founding Non-Profits · {preview.np_taken}/10</h3>
              {(preview.new_nonprofits || []).length === 0 ? (
                <p className="text-[#64748B] text-sm italic">None in this window.</p>
              ) : preview.new_nonprofits.map((f) => (
                <div key={f.vendor_id} className="py-1.5 text-sm flex gap-3">
                  <span className="text-emerald-400 font-bold w-8">#{f.founding_nonprofit_number}</span>
                  <span className="text-white flex-1">{f.business_name}</span>
                  <span className="text-[#9CA3AF] text-xs">{f.owner_name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#1E1E3A] border border-red-500/20 rounded-2xl p-5">
            <h3 className="text-red-300 font-syne font-bold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
              <AlertTriangle size={14} /> Action items
            </h3>
            <div className="text-sm text-[#E5E7EB] space-y-1">
              <div>{preview.pending_ein_reviews} EIN verification{preview.pending_ein_reviews === 1 ? '' : 's'} awaiting review.</div>
              <div>{preview.error_count} signup issue{preview.error_count === 1 ? '' : 's'} to triage.</div>
              <div>{preview.new_complaints} new complaint{preview.new_complaints === 1 ? '' : 's'}.</div>
            </div>
          </div>
        </>
      )}

      {/* Past runs */}
      <div className="bg-[#1E1E3A] border border-white/10 rounded-2xl p-5">
        <h3 className="text-white font-syne font-bold text-lg mb-3 flex items-center gap-2">
          <Clock size={16} /> Past sends
        </h3>
        {runs.length === 0 ? (
          <p className="text-[#64748B] text-sm italic">No digest runs recorded yet.</p>
        ) : (
          <div className="divide-y divide-white/5 text-sm" data-testid="digest-runs">
            {runs.map((r, i) => (
              <div key={i} className="py-2 flex items-center gap-4">
                <span className="text-[#9CA3AF] font-mono text-xs w-40">{(r.sent_at || '').slice(0, 19).replace('T', ' ')}</span>
                <span className="text-white flex-1">{r.recipient}</span>
                <span className="text-emerald-400 font-mono text-xs">${(r.receipts_total || 0).toFixed(2)}</span>
                <span className="text-[#C4B5FD] text-xs">{r.receipts_count} receipts</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${r.email_status === 'sent' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{r.email_status || '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, color }) {
  return (
    <div className="bg-[#1E1E3A] border border-white/10 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-[#9CA3AF] text-xs uppercase tracking-wider">
        {icon} {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
